import { Apy } from 'components/Apy'
import { DynamicSizedLoadingRow } from 'components/Loader/styled'
import MultiCurrencyLogo from 'components/MultiCurrencyLogo'
import { MouseoverTooltip } from 'components/Tooltip'
import { ISLAND_BONUS_INFO } from 'constants/lists'
import { useTokens } from 'hooks/Tokens'
import { useKodiakFarm, useKodiakFarmAverageApr } from 'hooks/useKodiakFarm'
import { useRewardVaultDatas } from 'hooks/useRewardVault'
import useTheme from 'hooks/useTheme'
import React, { useMemo } from 'react'
import { Info } from 'react-feather'
import { Text } from 'rebass'
import { useBaultBonusInfo } from 'services/bonusInfo/hooks/useBaultBonusInfo'
import { IslandApiResponse } from 'state/data/slice'
import styled, { keyframes } from 'styled-components'
import { ThemedText } from 'theme'
import { formatAmount } from 'utils/formatDollarAmt'
import { Address } from 'viem'

import { ReactComponent as Star } from '../../../../assets/svg/star.svg'
import * as S from '../styled'
import MultiplierTooltipContent from './MultiplierTooltipContent'
import { BonusIconWrapper, EnhancedStarIcon, RangeLineItem } from './styled'

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`

const VaultAPYContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`

export interface APRData {
  swapFeeAPR: number
  farmAPR: number | undefined
  rewardTokens: any[] | undefined
  activeRewardTokens?: any[]
  showSwapFeeAPR?: boolean
  showRewardTokens?: boolean
  isGeneric?: boolean
  isRewardVault?: boolean
  rewardVaultRewardToken?: any
  vaultAPY?: number
  bonusInfo?: {
    show: boolean
    multipliers?: string[]
    extraInfo?: string
    icon?: React.ReactNode
    vaultAPY?: number
  }
}

type APRContentVariant = 'standard' | 'portfolio'

// Shared UI components between both APR implementations

// Memoized loading skeleton
const LoadingSkeleton = React.memo(() => (
  <DynamicSizedLoadingRow
    style={{ width: '50%' }}
    height="24px"
    borderRadius="4px"
    borderTopLeftRadius="0"
    borderTopRightRadius="0"
    borderBottomLeftRadius="0"
    borderBottomRightRadius="0"
  >
    <div />
  </DynamicSizedLoadingRow>
))
LoadingSkeleton.displayName = 'LoadingSkeleton'

const FarmApr = React.memo(({ farmAPR }: { farmAPR: number }) => {
  const theme = useTheme()
  return <span style={{ color: theme.buttonPrimary }}>{formatAmount(farmAPR.toFixed(2))}%</span>
})
FarmApr.displayName = 'FarmApr'

const BonusIcon = React.memo(({ type, color }: { type: string; color: string }) => {
  const size = 16

  if (type === 'star') {
    return (
      <BonusIconWrapper>
        <EnhancedStarIcon color={color}>
          <Star style={{ fill: color, width: size, height: size }} />
        </EnhancedStarIcon>
      </BonusIconWrapper>
    )
  }

  return (
    <BonusIconWrapper>
      <Info size={size} color={color} />
    </BonusIconWrapper>
  )
})
BonusIcon.displayName = 'BonusIcon'

// Cache for bonus info
const poolBonusCache = new Map()

/**
 * IslandAPRColumn - Optimized for Island API data
 * Uses API data directly without on-chain calls
 */
export const IslandAPRColumn = React.memo(({ island }: { island: IslandApiResponse | null }) => {
  // Get raw reward token IDs from farm data
  const rewardTokenIds = useMemo(() => island?.farm?.rewardTokens?.map((t) => t.id.toLowerCase()) || [], [island?.farm])

  // Get full token objects for all reward tokens
  const allRewardTokens = useTokens(rewardTokenIds)

  // Determine actively earning reward tokens
  const activeRewardTokenIds = useMemo(() => {
    if (!island?.farm) return []

    // For reward vaults, use all reward tokens
    if (island.isRewardVault) {
      return rewardTokenIds
    }

    // For regular farms, filter by active rewards
    return island.farm.rewardTokens
      .filter((_, index) => BigInt(island.farm?.rewardRates?.[index] || '0') > 0n)
      .map((t) => t.id.toLowerCase())
  }, [island?.farm, island?.isRewardVault, rewardTokenIds])

  // Get full token objects for active rewards
  const activeRewardTokens = useTokens(activeRewardTokenIds)

  const hasFarm = !!island?.farm

  const baultBonusInfo = useBaultBonusInfo(island)

  const legacyBonusInfo = useMemo(() => {
    if (!island?.id) return { show: false }

    // Check cache first
    const cachedInfo = poolBonusCache.get(island.id)
    if (cachedInfo) return cachedInfo

    // Get from constants
    const info = ISLAND_BONUS_INFO[island.id.toLowerCase()]
    const bonusInfo = info ? { show: true, ...info } : { show: false }

    // Cache for future use
    poolBonusCache.set(island.id, bonusInfo)
    return bonusInfo
  }, [island?.id])

  // Prioritize Bault bonus, then legacy bonus
  const finalBonusInfo = useMemo(() => {
    if (baultBonusInfo?.show) return baultBonusInfo
    if (legacyBonusInfo.show) return legacyBonusInfo
    return { show: false }
  }, [baultBonusInfo, legacyBonusInfo])

  const aprData = useMemo(() => {
    if (!island) return null

    const baseData = {
      swapFeeAPR: island.apr || 0,
      isRewardVault: island.isRewardVault || false,
      totalApr: island.totalApr || 0,
    }

    // Farm-specific data
    const farmData = hasFarm
      ? {
          farmAPR: island.farm?.apr || 0,
          rewardTokens: allRewardTokens.filter(Boolean),
          activeRewardTokens: activeRewardTokens.filter(Boolean),
          rewardVaultRewardToken: allRewardTokens[0] || null,
        }
      : {}

    // Bonus info handling
    let bonusInfo = poolBonusCache.get(island.id)
    if (!bonusInfo) {
      const info = ISLAND_BONUS_INFO[island.id?.toLowerCase()]
      bonusInfo = info ? { show: true, ...info } : { show: false }
      poolBonusCache.set(island.id, bonusInfo)
    }

    const vaultAPY = finalBonusInfo?.apy

    return {
      ...baseData,
      ...farmData,
      bonusInfo: finalBonusInfo,
      vaultAPY,
      isGeneric: false,
    }
  }, [island, hasFarm, allRewardTokens, activeRewardTokens, finalBonusInfo])

  if (!aprData) {
    return (
      <RangeLineItem>
        <LoadingSkeleton />
      </RangeLineItem>
    )
  }

  return (
    <RangeLineItem>
      <APRContent aprData={aprData as APRData} />
    </RangeLineItem>
  )
})

IslandAPRColumn.displayName = 'IslandAPRColumn'

// Memoized tooltip text content component
const TooltipText = React.memo(
  ({
    swapFeeAPR,
    farmAPR,
    isGeneric,
    vaultAPY,
  }: {
    swapFeeAPR: number
    farmAPR: number | undefined
    isGeneric: boolean
    vaultAPY?: number
  }) => {
    const theme = useTheme()

    return (
      <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
        {!isGeneric && `Swap Fees: ${parseFloat(swapFeeAPR.toString())?.toFixed(2)}%`}
        {!isGeneric && farmAPR !== undefined && <br />}
        <br />
        {farmAPR !== undefined && (
          <>
            Farm APR: <FarmApr farmAPR={farmAPR} />
          </>
        )}
        {vaultAPY?.toString() && <br />}
        <br />
        {vaultAPY?.toString() && (
          <>
            Bault APY:{' '}
            <Text color="#10b981" style={{ display: 'inline-block' }}>
              {vaultAPY.toFixed(2)}%
            </Text>
          </>
        )}
      </ThemedText.DeprecatedBody>
    )
  }
)
TooltipText.displayName = 'TooltipText'

const PortfolioTooltipText = React.memo(
  ({
    swapFeeAPR,
    farmAPR,
    vaultAPY,
    showSwapFeeAPR,
  }: {
    swapFeeAPR: number
    farmAPR: number | undefined
    vaultAPY?: number
    showSwapFeeAPR?: boolean
  }) => {
    const theme = useTheme()
    const showSwapFees = showSwapFeeAPR !== false
    const sections: React.ReactNode[] = []

    if (showSwapFees) {
      sections.push(`Swap Fees: ${parseFloat(swapFeeAPR.toString())?.toFixed(2)}%`)
    }
    if (farmAPR !== undefined) {
      sections.push(
        <>
          Farm APR: <FarmApr farmAPR={farmAPR} />
        </>
      )
    }
    if (vaultAPY?.toString()) {
      sections.push(
        <>
          Bault APY:{' '}
          <Text color="#10b981" style={{ display: 'inline-block' }}>
            {vaultAPY.toFixed(2)}%
          </Text>
        </>
      )
    }

    return (
      <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
        {sections.map((section, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <>
                <br />
                <br />
              </>
            )}
            {section}
          </React.Fragment>
        ))}
      </ThemedText.DeprecatedBody>
    )
  }
)
PortfolioTooltipText.displayName = 'PortfolioTooltipText'

const APRDisplay = React.memo(
  ({ swapFeeAPR, farmAPR, isGeneric }: { swapFeeAPR: number; farmAPR: number | undefined; isGeneric: boolean }) => {
    const theme = useTheme()
    return (
      <ThemedText.DeprecatedBody fontSize="14px" fontWeight="400" lineHeight="24px" color={theme.textSecondary}>
        {isGeneric ? (
          <FarmApr farmAPR={farmAPR || 0} />
        ) : farmAPR !== undefined ? (
          <>
            {`${parseFloat(swapFeeAPR.toString())?.toFixed(2)}% + `}
            <FarmApr farmAPR={farmAPR} />
          </>
        ) : (
          `${swapFeeAPR?.toFixed(2)}%`
        )}
      </ThemedText.DeprecatedBody>
    )
  }
)
APRDisplay.displayName = 'APRDisplay'

const PortfolioAPRDisplay = React.memo(
  ({
    swapFeeAPR,
    farmAPR,
    showSwapFeeAPR,
  }: {
    swapFeeAPR: number
    farmAPR: number | undefined
    showSwapFeeAPR?: boolean
  }) => {
    const theme = useTheme()
    const showSwapFees = showSwapFeeAPR !== false
    return (
      <ThemedText.DeprecatedBody fontSize="14px" fontWeight="400" lineHeight="24px" color={theme.textSecondary}>
        {farmAPR !== undefined ? (
          showSwapFees ? (
            <>
              {`${parseFloat(swapFeeAPR.toString())?.toFixed(2)}% + `}
              <FarmApr farmAPR={farmAPR} />
            </>
          ) : (
            <FarmApr farmAPR={farmAPR} />
          )
        ) : showSwapFees ? (
          `${swapFeeAPR?.toFixed(2)}%`
        ) : null}
      </ThemedText.DeprecatedBody>
    )
  }
)
PortfolioAPRDisplay.displayName = 'PortfolioAPRDisplay'

const TokenDisplay = React.memo(
  ({
    activeRewardTokens,
  }: {
    isRewardVault: boolean
    rewardVaultRewardToken: any
    activeRewardTokens: any[] | undefined
  }) => {
    if (!activeRewardTokens || activeRewardTokens.length === 0) {
      return <LoadingSkeleton />
    }

    if (activeRewardTokens && activeRewardTokens.length > 0) {
      return (
        <div style={{ marginTop: '12px' }}>
          <MultiCurrencyLogo size={21} margin={true} currencies={[...activeRewardTokens]} />
        </div>
      )
    }

    return null
  }
)
TokenDisplay.displayName = 'TokenDisplay'

// Memoized BonusDisplay component
const BonusDisplay = React.memo(({ bonusInfo }: { bonusInfo: APRData['bonusInfo'] }) => {
  const theme = useTheme()

  if (!bonusInfo || !bonusInfo.show || !bonusInfo.multipliers || bonusInfo.multipliers.length === 0) {
    return null
  }

  return (
    <MouseoverTooltip
      placement="top"
      text={<MultiplierTooltipContent title={bonusInfo.extraInfo} multipliers={bonusInfo.multipliers} />}
    >
      <BonusIcon
        type={bonusInfo.icon?.toString() || 'star'}
        color={theme.accentAction || theme.deprecated_yellow3 || '#FFD700'}
      />
    </MouseoverTooltip>
  )
})
BonusDisplay.displayName = 'BonusDisplay'

const StandardAPRContent = React.memo(({ aprData }: { aprData: APRData }) => {
  const {
    swapFeeAPR,
    farmAPR,
    activeRewardTokens,
    isGeneric,
    isRewardVault,
    rewardVaultRewardToken,
    bonusInfo,
    rewardTokens,
    vaultAPY,
  } = aprData

  return (
    <S.FlexCol style={{ gap: '4px' }}>
      <S.FlexRowCenter
        style={{
          gap: '4px',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
        }}
      >
        <MouseoverTooltip
          text={<TooltipText swapFeeAPR={swapFeeAPR} farmAPR={farmAPR} isGeneric={!!isGeneric} vaultAPY={vaultAPY} />}
        >
          <APRDisplay swapFeeAPR={swapFeeAPR} farmAPR={farmAPR} isGeneric={!!isGeneric} />
        </MouseoverTooltip>
        <div style={{ flexShrink: 0, maxWidth: '60px' }}>
          <TokenDisplay
            isRewardVault={!!isRewardVault}
            rewardVaultRewardToken={rewardVaultRewardToken}
            activeRewardTokens={activeRewardTokens}
          />
        </div>
      </S.FlexRowCenter>
      {vaultAPY?.toString() && (
        <div style={{ display: 'flex', alignSelf: 'end' }}>
          <Apy apy={vaultAPY?.toString()} />
        </div>
      )}
    </S.FlexCol>
  )
})
StandardAPRContent.displayName = 'StandardAPRContent'

const PortfolioAPRContent = React.memo(({ aprData }: { aprData: APRData }) => {
  const { swapFeeAPR, farmAPR, activeRewardTokens, vaultAPY, showSwapFeeAPR, showRewardTokens } = aprData
  const shouldShowSwapFees = showSwapFeeAPR !== false
  const shouldShowRewardTokens = showRewardTokens !== false && (activeRewardTokens?.length || 0) > 0
  const shouldShowAprDisplay = shouldShowSwapFees || farmAPR !== undefined
  const hasTooltip = shouldShowSwapFees || farmAPR !== undefined || vaultAPY !== undefined

  const content = (
    <S.FlexCol style={{ gap: '4px', alignItems: 'flex-end', width: '100%' }}>
      {(shouldShowAprDisplay || shouldShowRewardTokens) && (
        <S.FlexRowCenter
          style={{
            gap: '4px',
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
            width: '100%',
          }}
        >
          {shouldShowAprDisplay && (
            <PortfolioAPRDisplay swapFeeAPR={swapFeeAPR} farmAPR={farmAPR} showSwapFeeAPR={showSwapFeeAPR} />
          )}
          {shouldShowRewardTokens && (
            <div style={{ flexShrink: 0, maxWidth: '60px' }}>
              <div style={{ marginTop: '12px' }}>
                <MultiCurrencyLogo size={21} margin={true} currencies={[...(activeRewardTokens || [])]} />
              </div>
            </div>
          )}
        </S.FlexRowCenter>
      )}
      {vaultAPY?.toString() && (
        <div style={{ display: 'flex', alignSelf: 'flex-end' }}>
          <Apy apy={vaultAPY?.toString()} />
        </div>
      )}
    </S.FlexCol>
  )

  if (!hasTooltip) {
    return content
  }

  return (
    <MouseoverTooltip
      text={
        <PortfolioTooltipText
          swapFeeAPR={swapFeeAPR}
          farmAPR={farmAPR}
          vaultAPY={vaultAPY}
          showSwapFeeAPR={showSwapFeeAPR}
        />
      }
    >
      {content}
    </MouseoverTooltip>
  )
})
PortfolioAPRContent.displayName = 'PortfolioAPRContent'

export const APRContent = React.memo(
  ({ aprData, variant = 'standard' }: { aprData: APRData; variant?: APRContentVariant }) => {
    if (variant === 'portfolio') {
      return <PortfolioAPRContent aprData={aprData} />
    }
    return <StandardAPRContent aprData={aprData} />
  }
)
APRContent.displayName = 'APRContent'

const poolDataCache = new Map()

/**
 * APRColumn - Legacy implementation that uses on-chain calls
 * Used for both V2 Pools and SubgraphIslands
 */
export const APRColumn = React.memo(
  ({
    pool,
    type,
    farmAddress,
    isGeneric,
    isRewardVault,
    showOnlyFarmAPR = false,
  }: {
    pool: IslandApiResponse | null
    type?: 'Generic' | 'V2Pool' | 'IslandPool'
    farmAddress: Address | undefined
    isGeneric?: boolean
    isRewardVault?: boolean
    showOnlyFarmAPR?: boolean
  }) => {
    // Get addresses (memoized to prevent unnecessary re-renders)
    const effectiveFarmAddress = useMemo(
      () => (isRewardVault && !showOnlyFarmAPR ? undefined : farmAddress),
      [isRewardVault, showOnlyFarmAPR, farmAddress]
    )

    // Get vault data only when needed
    const {
      apr: rewardVaultAPR,
      isLoading: isLoadingRewardVault,
      data: rewardVaultData,
    } = useRewardVaultDatas(isRewardVault ? farmAddress : undefined)

    // Get farm data only when needed
    const { data: farm, isLoading: isLoadingFarm } = useKodiakFarm(effectiveFarmAddress)
    const { apr: farmAPR, isLoading: isLoadingFarmAPR } = useKodiakFarmAverageApr(farm)

    // Memoize bonus info to prevent recalculation
    const bonusInfo = useMemo(() => {
      if (!pool?.id) return { show: false }
      // Check if we already have this in the cache
      if (poolDataCache.has(pool.id)) {
        return poolDataCache.get(pool.id)
      }
      // Calculate and cache the result
      const info = ISLAND_BONUS_INFO[pool.id.toLowerCase()]
      const result = info ? { show: true, ...info } : { show: false }
      poolDataCache.set(pool.id, result)
      return result
    }, [pool?.id])

    // Calculate swap fee APR only once
    const swapFeeAPR = useMemo(() => {
      if (!pool || type === 'Generic') return 0
      return pool.apr
    }, [pool, type])

    // Filter active reward tokens efficiently
    const activeRewardTokens = useMemo(() => {
      if (isRewardVault && !showOnlyFarmAPR) {
        return rewardVaultData?.rewardToken && (rewardVaultData.rewardRate ?? 0) > 0
          ? [rewardVaultData.rewardToken]
          : []
      }
      if (!farm?.rewardTokens || !farm.rewardRates) return []

      // Filter tokens with non-zero rewards
      return farm.rewardTokens.filter((_, index) => BigInt(farm.rewardRates?.[index] || '0') > 0n).map((t) => t.id)
    }, [
      isRewardVault,
      showOnlyFarmAPR,
      farm?.rewardTokens,
      farm?.rewardRates,
      rewardVaultData?.rewardToken,
      rewardVaultData?.rewardRate,
    ])

    // Determine loading state
    const isLoading = isRewardVault ? isLoadingRewardVault : isLoadingFarm || isLoadingFarmAPR

    // Create aprData object only when dependencies change
    const aprData = useMemo(
      () => ({
        swapFeeAPR,
        farmAPR: isRewardVault && !showOnlyFarmAPR ? rewardVaultAPR || 0 : farmAPR,
        rewardTokens: isRewardVault
          ? rewardVaultData?.rewardToken
            ? [rewardVaultData.rewardToken]
            : []
          : farm?.rewardTokens || [],
        activeRewardTokens,
        isGeneric,
        isRewardVault,
        rewardVaultRewardToken: isRewardVault ? rewardVaultData?.rewardToken : undefined,
        bonusInfo,
      }),
      [
        swapFeeAPR,
        isRewardVault,
        showOnlyFarmAPR,
        rewardVaultAPR,
        farmAPR,
        rewardVaultData?.rewardToken,
        farm?.rewardTokens,
        activeRewardTokens,
        isGeneric,
        bonusInfo,
      ]
    )

    // Early return if no farm address or reward vault address
    if (!farmAddress && !isRewardVault) {
      return (
        <RangeLineItem>
          <APRContent aprData={{ ...aprData, farmAPR: undefined, activeRewardTokens: [] }} />
        </RangeLineItem>
      )
    }

    // Loading state
    if (isLoading) {
      return (
        <RangeLineItem>
          <LoadingSkeleton />
        </RangeLineItem>
      )
    }

    // Render with data
    return (
      <RangeLineItem>
        <APRContent aprData={aprData} />
      </RangeLineItem>
    )
  },
  (prevProps, nextProps) => {
    // Compare only what matters for rendering
    const prevPool = prevProps.pool
    const nextPool = nextProps.pool

    // Handle null pools
    if (!prevPool && !nextPool) {
      return (
        prevProps.type === nextProps.type &&
        prevProps.farmAddress === nextProps.farmAddress &&
        prevProps.isRewardVault === nextProps.isRewardVault &&
        prevProps.showOnlyFarmAPR === nextProps.showOnlyFarmAPR &&
        prevProps.isGeneric === nextProps.isGeneric
      )
    }

    if (prevPool?.id !== nextPool?.id) return false

    const typeEqual = prevProps.type === nextProps.type
    const poolAPREqual = (!prevPool?.apr && !nextPool?.apr) || prevPool?.apr === nextPool?.apr
    const farmAddressEqual = prevProps.farmAddress === nextProps.farmAddress
    const rewardVaultEqual = prevProps.isRewardVault === nextProps.isRewardVault
    const showOnlyFarmEqual = prevProps.showOnlyFarmAPR === nextProps.showOnlyFarmAPR
    const isGenericEqual = prevProps.isGeneric === nextProps.isGeneric

    return typeEqual && poolAPREqual && farmAddressEqual && rewardVaultEqual && showOnlyFarmEqual && isGenericEqual
  }
)

APRColumn.displayName = 'APRColumn'

export default APRColumn
